/**
 * Слой замечаний «Юниверситета» (16.10.2026): корень вкладки, который ловит долгое нажатие (useHoldPress), понимает,
 * что зажато (locate), и открывает окно замечания. Черновики — по месту (якорь + цитата): закрыл окно, зажал то же
 * место снова — текст на месте. После отправки — короткое «Замечание отправлено».
 * Внутри — запрет выделения текста и системного меню на долгое нажатие (иначе iOS/Android перехватывают жест);
 * поля ввода выделяются как раньше.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { locate } from "./locate";
import { useHoldPress } from "./useHoldPress";
import { FeedbackSheet, type Pending } from "./FeedbackSheet";
import type { FbScope, FbTarget } from "./fbApi";
import css from "./feedback.module.css";

interface Props {
  className?: string;
  scope: FbScope;
  /** выключено на экране самих замечаний */
  enabled: boolean;
  children: ReactNode;
}

const keyOf = (t: FbTarget) => `${t.anchor}|${t.part}|${t.quote}|${t.media ?? ""}`;

export function FeedbackLayer({ className, scope, enabled, children }: Props) {
  const [target, setTarget] = useState<FbTarget | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const marked = useRef<Element | null>(null);
  const drafts = useRef(new Map<string, string>());
  const pendings = useRef(new Map<string, Pending>());   // ключ отправки с неизвестным исходом — по месту
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onHold = useCallback((el: Element) => {
    marked.current = el;
    setTarget(locate(el, scope.branchName));
  }, [scope.branchName]);
  const press = useHoldPress(onHold, enabled && !target);

  const unmark = () => { marked.current?.removeAttribute("data-fb-on"); marked.current = null; };
  const close = () => { unmark(); setTarget(null); };
  const sent = () => {
    close();
    window.clearTimeout(timer.current);
    setToast("Замечание отправлено. Свои замечания — в чипе «Замечания»");
    timer.current = window.setTimeout(() => setToast(null), 3500);
  };

  return (
    <div className={`${className ?? ""} ${css.zone}`} {...press}>
      {children}
      <FeedbackSheet target={target} scope={scope} draft={target ? drafts.current.get(keyOf(target)) ?? "" : ""}
        onDraft={(v) => { if (!target) return; if (v.trim()) drafts.current.set(keyOf(target), v); else drafts.current.delete(keyOf(target)); }}
        pending={target ? pendings.current.get(keyOf(target)) ?? null : null}
        onPending={(p) => { if (!target) return; if (p) pendings.current.set(keyOf(target), p); else pendings.current.delete(keyOf(target)); }}
        onClose={close} onSent={sent} />
      {toast ? <div className={css.toast} role="status" data-fb-skip="">{toast}</div> : null}
    </div>
  );
}
